import {
  extractSentryEventRequestMethod,
  extractSentryEventStatusCode,
  extractSentryEventUrlPath,
  type SentryEventLike,
} from './sentryEventFields';

/**
 * Drops the `HTTP Client Error` events raised by NetInfo's connectivity ping
 * (Sentry FRONTEND-2Z3 / 2ZB / 2ZD / 30D, TA-11010).
 *
 * `@react-native-community/netinfo` health-checks the connection on web by
 * sending a `HEAD` to `reachabilityUrl`, which defaults to the app's own
 * origin root. When that ping comes back 429 or 502 — which it does in
 * production, see NetInfoContext's note — `httpClientIntegration` reports it
 * as an unhandled error, attributed to whatever route the user happened to be
 * on (`/tasks`, `/projects/allprojects`, `/globalActivity`). Four Sentry
 * issues, ~1300 events and ~130 users of a probe doing exactly its job: the
 * library grades the response itself and the app reads the verdict through
 * `isInternetReachable`. There is nothing in the event for a frontend engineer
 * to act on, and the culprit route actively misleads — the TA-10121
 * alert-fatigue anti-pattern.
 *
 * The origin's own 429/502 rate is a backend/CDN signal and is monitored there
 * (TA-10770); dropping the client-side copy does not hide it.
 *
 * Deliberately narrow: only `HEAD` (the app issues no other HEAD request),
 * only the origin root, and only a failing status. A `HEAD` to any other path,
 * or any other method against `/`, still reports. Origin-agnostic so it covers
 * staging as well as production.
 */
export const shouldDropNetInfoReachabilityPing = (
  event: SentryEventLike
): boolean => {
  if (extractSentryEventRequestMethod(event) !== 'HEAD') return false;
  if (extractSentryEventUrlPath(event) !== '/') return false;

  const statusCode = extractSentryEventStatusCode(event);
  return typeof statusCode === 'number' && statusCode >= 400;
};
